import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import { Icons } from "assets";
import { toastMessage } from "shared/components/common/toast";
import styles from "./style.module.scss";
import classNames from "classnames";
import {
  CreateFaqSectionService,
  CreateQuestionService,
  GetAllSectionsService,
  UpdateQuestionService,
  UpdateSectionService,
} from "shared/services/faqs";
import FAQsListing from "shared/components/dashboard/dashboardListings/faqsListings";
import CustomSelect from "shared/components/common/customSelect";

export interface Question {
  id: number;
  title: string;
  detail: string;
}

const FAQs = () => {
  const [addSection, setAddSection] = useState(false);
  const [addQuestions, setAddQuestions] = useState(false);
  const [editingSection, setEditingSection] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [editingQuestion, setEditingQuestion] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedQuestions, setSelectedQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState<{ id: number; title: string }[]>([]);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await GetAllSectionsService();
        setSections(response.data.data || []);
      } catch (error) {
        console.error("Failed to fetch sections:", error);
      }
    };
    fetchSections();
  }, []);

  const SectionValidationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
  });
  const QuestionsValidationSchema = Yup.object({
    title: Yup.string().required("Question Title is required"),
    detail: Yup.string().required("Question detail is required"),
  });

  const sectionFormik = useFormik({
    initialValues: {
      title: "",
    },
    validationSchema: SectionValidationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (editingSection) {
          await UpdateSectionService(editingSectionId!, values.title);
          toastMessage("success", "Section updated successfully!");
        } else {
          await CreateFaqSectionService(values.title);
          toastMessage("success", "Section added successfully!");
        }
        resetStates();
      } catch (error) {
        console.error("Submission Error:", error);
        toastMessage("error", "Failed to save section.");
      } finally {
        setLoading(false);
      }
    },
  });

  const questionsFormik = useFormik({
    initialValues: {
      questionId: null, // Add this field to track if a question exists
      sectionId: "",
      title: "",
      detail: "",
    },
    validationSchema: QuestionsValidationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        if (values.questionId) {
          await UpdateQuestionService(
            values.questionId,
            values.title,
            values.detail
          );
          toastMessage("success", "Question updated successfully!");
        } else {
          await CreateQuestionService(
            // @ts-ignore
            values.sectionId,
            values.title,
            values.detail
          );
          toastMessage("success", "Question added successfully!");
        }

        resetStates();
      } catch (error) {
        console.error("Submission Error:", error);
        toastMessage("error", "Failed to save question.");
      } finally {
        setLoading(false);
      }
    },
  });

  const resetStates = () => {
    setAddSection(false);
    setAddQuestions(false);
    setEditingSection(false);
    setEditingQuestion(false);
    setSelectedSection(null);
    setSelectedQuestions([]);
    sectionFormik.resetForm();
    questionsFormik.resetForm();
  };

  const handleCancel = () => {
    resetStates();
  };
  const handleUpdateSection = (sectionId: number, sectionTitle: string) => {
    setEditingSection(true);
    setEditingSectionId(sectionId);
    sectionFormik.setValues({ title: sectionTitle });
    setAddSection(true);
  };

  const handleUpdateQuestion = (sectionId: number, question: Question) => {
    setEditingQuestion(true);

    questionsFormik.setValues({
      // @ts-ignore
      questionId: question.id,
      sectionId: sectionId.toString(),
      title: question.title,
      detail: question.detail,
    });

    setAddQuestions(true);
  };

  return (
    <div className={classNames(styles.sectionContainer)}>
      {addSection ? (
        <>
          <div>
            <CustomButton
              Icon={Icons.ArrowLeft}
              IconDirection="left"
              iconStyle="mr-2"
              title="Back"
              containerStyle="outlined-btn-primary mb-3 px-2"
              onClick={handleCancel}
            />
          </div>
          <form
            onSubmit={sectionFormik.handleSubmit}
            className={classNames(styles.body, "grid grid-cols-12 gap-2")}
          >
            <div className="col-span-6">
              <CustomInput
                label="Title"
                required
                placeholder="Enter section title"
                name="title"
                value={sectionFormik.values.title}
                onChange={sectionFormik.handleChange}
                onBlur={sectionFormik.handleBlur}
                error={
                  sectionFormik.touched.title && sectionFormik.errors.title
                    ? sectionFormik.errors.title
                    : ""
                }
              />
            </div>
            <div className="flex justify-end mt-3 w-full col-span-12">
              <CustomButton
                type="submit"
                loading={loading}
                title={editingSection ? "Update Section" : "Add Section"}
              />
            </div>
          </form>
        </>
      ) : addQuestions ? (
        <>
          <div>
            <CustomButton
              Icon={Icons.ArrowLeft}
              IconDirection="left"
              iconStyle="mr-2"
              title="Back"
              containerStyle="outlined-btn-primary mb-3 px-2"
              onClick={handleCancel}
            />
          </div>
          <form
            onSubmit={questionsFormik.handleSubmit}
            className={classNames(styles.body, "grid grid-cols-12 gap-2")}
          >
            <div className="col-span-6">
              <CustomSelect
                label="Select Section"
                required
                options={sections.map((section) => ({
                  value: String(section.id),
                  label: section.title,
                  id: section.id,
                  title: section.title,
                }))}
                value={String(questionsFormik.values.sectionId)}
                onChange={(option) => {
                  questionsFormik.setFieldValue("sectionId", option.id);
                }}
                error={
                  questionsFormik.touched.sectionId &&
                  questionsFormik.errors.sectionId
                    ? questionsFormik.errors.sectionId
                    : ""
                }
              />
            </div>
            <div className="col-span-12">
              <CustomInput
                label="Question Title"
                required
                placeholder="Enter question title"
                name="title"
                value={questionsFormik.values.title}
                onChange={questionsFormik.handleChange}
                onBlur={questionsFormik.handleBlur}
                error={
                  questionsFormik.touched.title && questionsFormik.errors.title
                    ? questionsFormik.errors.title
                    : ""
                }
              />
            </div>
            <div className="col-span-12">
              <CustomInput
                label="Detail"
                required
                placeholder="Enter question detail"
                name="detail"
                value={questionsFormik.values.detail}
                onChange={questionsFormik.handleChange}
                onBlur={questionsFormik.handleBlur}
                error={
                  questionsFormik.touched.detail &&
                  questionsFormik.errors.detail
                    ? questionsFormik.errors.detail
                    : ""
                }
              />
            </div>

            <div className="flex justify-end mt-3 w-full col-span-12">
              <CustomButton
                type="submit"
                loading={loading}
                title="Submit Question"
              />
            </div>
          </form>
        </>
      ) : (
        <div className={classNames(styles.header)}>
          <div className="ms-auto w-fit flex gap-4">
            <CustomButton
              onClick={() => setAddSection(true)}
              title="Add Section"
            />
            <CustomButton
              onClick={() => setAddQuestions(true)}
              title="Add Questions"
            />
          </div>
          <div className="mt-4">
            <FAQsListing
              // @ts-ignore
              handleUpdateSection={(id, title) =>
                handleUpdateSection(id, title)
              }
              handleUpdateQuestion={(id, question) =>
                handleUpdateQuestion(id, question)
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQs;
